/**
 * ArchivePage — /event/:slug/archive
 *
 * The canonical resting place for memory objects. The shelf-of-spines
 * is anchored on a quiet wood surface; pulling a spine opens an
 * in-page reading overlay (chapter PNG / letter HTML).
 *
 * Tone rules:
 *   • "Tonight's archive ✦" header (NEVER "Files" or "History")
 *   • Single soft event-name line below the eyebrow
 *   • Below the shelf — a quiet line of "objects" count in serif
 *   • Empty state remains dignified (see Bookshelf component)
 *
 * Routes from:
 *   • EventPage dashboard widget (host quick-jump)
 *   • Recap email "Revisit the wall" → Memory Wall → ghost-pill tap
 *   • Direct URL share (guests' organic return)
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CaretLeft } from '@phosphor-icons/react';
import Bookshelf from '../components/Bookshelf';
import ArchiveReflection from '../components/ArchiveReflection';
import { resolveDashboardHref } from '../shared/guestSession';
import useScrollRestore from '../shared/navigation/useScrollRestore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArchivePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // keepsake/guest_id may be carried from the recap email so the
  // letter-reader can render the recipient's actual letter.
  const keepsakeGid = searchParams.get('keepsake') || searchParams.get('g') || null;
  // focus token carried from the wall's "settled on the shelf" pill —
  // gives the named chapter spine a soft entrance glow so the
  // transition from wall → archive feels physically adjacent.
  const focusId = searchParams.get('focus') || null;

  const [archive, setArchive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reading, setReading] = useState(null);  // {type, obj}

  // iter197 — Restore the shelf scroll position on back so a guest
  // returning from an opened spine lands on the same shelf row.
  useScrollRestore(archive?.event_id ? `archive_${archive.event_id}` : (slug ? `archive_slug_${slug}` : null));

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const ev = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        const r = await axios.get(`${API}/events/${ev.data.event_id}/archive`);
        if (!cancelled) setArchive(r.data);
        // Lifecycle Observations — fire the host-archive-visit
        // ping. The backend enforces auth + dormancy; anonymous
        // / guest sessions simply return 200 and never emit.
        try {
          const tok = localStorage.getItem('session_token');
          if (tok) {
            axios.post(
              `${API}/events/${ev.data.event_id}/archive/host-visit`,
              {},
              { headers: { Authorization: `Bearer ${tok}` } },
            ).catch(() => {});
          }
        } catch { /* noop */ }
      } catch {
        if (!cancelled) setArchive(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const closeReader = () => setReading(null);

  // Tap a chapter spine → opens the chapter PNG inside a museum-like overlay.
  const openChapter = (obj) => setReading({ type: 'chapter', obj });
  // Tap a letter → opens the recap letter HTML (validated by the
  // backend against keepsakeGid; anonymous viewers get a dignified
  // placeholder instead of the private body).
  const openLetter = (obj) => setReading({ type: 'letter', obj });
  // Tap a poster portfolio → opens the rendered poster PNG inside the
  // same museum-like overlay. Poster + chapter share an identical
  // reader so the archive feels like one collection, not three
  // different feature areas.
  const openPoster = (obj) => setReading({ type: 'poster', obj });

  const eventName = archive?.event_name || 'this night';

  return (
    <div
      data-testid="archive-page"
      style={{
        minHeight: '100vh',
        background: '#0F0B1A',
        backgroundImage: `
          radial-gradient(ellipse at 50% 0%, rgba(212,175,94,0.06) 0%, transparent 45%),
          radial-gradient(ellipse at 50% 100%, rgba(20,12,6,0.5) 0%, transparent 50%)
        `,
        color: '#FFE7A8',
      }}
    >
      {/* Cinematic back pill — soft glass, restraint */}
      <button
        type="button"
        onClick={() => navigate(resolveDashboardHref(slug))}
        data-testid="archive-back-btn"
        style={{
          position: 'absolute', top: 18, left: 18, zIndex: 5,
          display: 'inline-flex', alignItems: 'center', gap: 8,
          height: 38, padding: '0 14px', borderRadius: 999,
          background: 'rgba(20,12,6,0.55)',
          border: '1px solid rgba(212,175,94,0.20)',
          color: '#E2C98E',
          fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 13,
          cursor: 'pointer', backdropFilter: 'blur(10px)',
        }}
      >
        <CaretLeft size={14} weight="bold" /> Back
      </button>

      {/* A single tucked reflection — discovered, never broadcast. */}
      <ArchiveReflection eventId={archive?.event_id} />

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '72px 28px 80px' }}>
        {/* Header — restrained, archival */}
        <div style={{ textAlign: 'left', marginBottom: 28 }}>
          <div style={{
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: 'rgba(212,175,94,0.82)', marginBottom: 10,
          }}>
            Tonight's archive ✦
          </div>
          <h1 style={{
            fontFamily: 'Georgia, serif',
            fontSize: 'clamp(28px, 4.5vw, 44px)',
            fontWeight: 500, margin: 0, letterSpacing: '-0.015em',
            color: '#FFE7A8', textShadow: '0 0 28px rgba(212,175,94,0.30)',
            lineHeight: 1.1,
          }}>
            {loading ? 'composing the shelf…' : eventName}
          </h1>
          <p style={{
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            color: 'rgba(255,235,210,0.65)', fontSize: 15,
            marginTop: 10, maxWidth: 560, lineHeight: 1.55,
          }}>
            A quiet place where the chapters, letters and posters from this night rest. Pull one from the shelf to revisit.
          </p>
        </div>

        {/* The shelf */}
        <Bookshelf
          eventId={archive?.event_id}
          slug={slug}
          variant="full"
          shelfHeight={360}
          data={archive || undefined}
          guestId={keepsakeGid}
          focusId={focusId}
          onPullChapter={openChapter}
          onPullLetter={openLetter}
          onPullPoster={openPoster}
        />

        {/* Soft objects line — feels editorial, never analytics */}
        {!loading && archive && (
          <div style={{
            marginTop: 18, textAlign: 'center',
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            color: 'rgba(255,235,210,0.42)', fontSize: 12, letterSpacing: '0.06em',
          }}>
            {archive.object_count === 0
              ? 'awaiting its first chapter ✦'
              : archive.object_count === 1
                ? 'one object preserved on the shelf ✦'
                : `${archive.object_count} objects preserved on the shelf ✦`}
          </div>
        )}
      </div>

      {/* READING OVERLAY — chapter PNG or letter HTML */}
      {reading && (
        <div
          data-testid="archive-reader"
          onClick={closeReader}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(6,3,1,0.86)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
            animation: 'archiveReaderFade 320ms ease-out',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(900px, 100%)',
              maxHeight: '92vh',
              background: '#1a1108',
              borderRadius: 16,
              border: '1px solid rgba(212,175,94,0.30)',
              boxShadow: '0 40px 100px rgba(0,0,0,0.6)',
              overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{
              padding: '16px 22px',
              borderBottom: '1px solid rgba(212,175,94,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12,
              background: 'linear-gradient(180deg, rgba(40,28,14,0.55) 0%, rgba(20,12,6,0.55) 100%)',
            }}>
              <div>
                <div style={{
                  fontFamily: 'Georgia, serif', fontStyle: 'italic',
                  fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: '#A8854C', marginBottom: 4,
                }}>
                  {reading.type === 'letter' ? 'A preserved letter' :
                   reading.type === 'poster' ? 'A preserved poster' :
                   'A preserved chapter'}
                </div>
                <div style={{
                  fontFamily: 'Georgia, serif', fontSize: 18, color: '#FFE7A8',
                  letterSpacing: '-0.005em',
                }}>
                  {reading.obj.name}
                </div>
                {reading.obj.mood && (
                  <div style={{
                    fontFamily: 'Georgia, serif', fontStyle: 'italic',
                    fontSize: 12, color: '#C9A87A', marginTop: 4,
                  }}>
                    {reading.obj.mood}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={closeReader}
                data-testid="archive-reader-close"
                aria-label="Return to the shelf"
                style={{
                  background: 'transparent', border: 'none', color: '#C9A87A',
                  fontSize: 22, cursor: 'pointer', padding: '4px 8px',
                }}
              >×</button>
            </div>

            <div style={{
              flex: 1, minHeight: 320,
              background: reading.type === 'letter' ? '#FAF6EE' :
                         reading.type === 'poster' ? '#FAF6EE' :
                         '#100804',
              overflow: 'auto',
            }}>
              {reading.type === 'chapter' && reading.obj.preview_url && (
                <img
                  data-testid="archive-chapter-image"
                  src={reading.obj.preview_url}
                  alt=""
                  style={{ display: 'block', width: '100%', height: 'auto' }}
                />
              )}
              {reading.type === 'poster' && reading.obj.preview_url && (
                <img
                  data-testid="archive-poster-image"
                  src={reading.obj.preview_url}
                  alt=""
                  style={{ display: 'block', width: '100%', height: 'auto' }}
                />
              )}
              {reading.type === 'letter' && archive?.event_id && (
                <iframe
                  data-testid="archive-letter-iframe"
                  title="Letter from this night"
                  src={`${API}/events/${archive.event_id}/memory-recap/letter${keepsakeGid ? `?guest_id=${keepsakeGid}` : ''}`}
                  sandbox=""
                  style={{ width: '100%', height: '70vh', border: 0, background: '#FAF6EE' }}
                />
              )}
            </div>

            {(reading.type === 'chapter' || reading.type === 'poster') && reading.obj.download_url && (
              <div style={{
                padding: '12px 22px',
                borderTop: '1px solid rgba(212,175,94,0.18)',
                background: '#140C06',
                textAlign: 'center',
              }}>
                <a
                  data-testid={`archive-${reading.type}-download`}
                  href={reading.obj.download_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: '#FFE7A8', fontFamily: 'Georgia, serif', fontStyle: 'italic',
                    fontSize: 14, textDecoration: 'underline',
                    textDecorationColor: 'rgba(212,175,94,0.28)',
                    textUnderlineOffset: 3,
                  }}
                >
                  Continue reading the printed version ✦
                </a>
              </div>
            )}
          </div>

          <style>{`
            @keyframes archiveReaderFade {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
